import { Avatar, AvatarFallback } from "#components/ui/avatar"
import { Badge } from "#components/ui/badge"
import { Button } from "#components/ui/button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "#components/ui/collapsible"
import { cn } from "#lib/utils"
import type { ChatMessage as ChatMessageType } from "@home-finance-manager/shared/types/chat"
import { Bot, ChevronDown, ChevronRight, Code2, User, Wrench } from "lucide-react"
import { useState } from "react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"

interface ChatMessageProps {
  message: ChatMessageType
}

export function ChatMessage({ message }: ChatMessageProps) {
  const isUser = message.role === "user"
  const isAssistant = message.role === "assistant"
  const isTool = message.role === "tool"

  const [toolCallsOpen, setToolCallsOpen] = useState(false)
  const [toolResultOpen, setToolResultOpen] = useState(false)

  const avatar = isUser ? (
    <Avatar>
      <AvatarFallback>
        <User className="h-4 w-4" />
      </AvatarFallback>
    </Avatar>
  ) : isTool ? (
    <Avatar>
      <AvatarFallback>
        <Wrench className="h-4 w-4" />
      </AvatarFallback>
    </Avatar>
  ) : (
    <Avatar>
      <AvatarFallback>
        <Bot className="h-4 w-4" />
      </AvatarFallback>
    </Avatar>
  )

  const hasToolCalls = message.toolCalls && message.toolCalls.length > 0
  const hasContent = message.content && message.content.trim().length > 0

  const shouldShowToolCalls = isAssistant && hasToolCalls && !hasContent
  const shouldShowContent = hasContent && !isTool && (!isAssistant || !hasToolCalls)

  if (isAssistant && !hasContent && !hasToolCalls) {
    return null
  }

  let toolResultData: unknown = null
  let isJsonResult = false
  if (isTool && message.content) {
    try {
      toolResultData = JSON.parse(message.content)
      isJsonResult = true
    } catch {}
  }

  return (
    <div className={cn("flex gap-3 min-w-0 mb-4", isUser ? "flex-row-reverse" : "flex-row")}>
      {avatar}
      <div className={cn("flex-1 space-y-2 min-w-0", isUser ? "items-end" : "items-start")}>
        {shouldShowToolCalls && (
          <div className="mb-2">
            <Collapsible open={toolCallsOpen} onOpenChange={setToolCallsOpen}>
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto px-2.5 py-2 text-xs font-medium hover:bg-muted/60 w-full justify-start rounded-md border border-border/50 bg-muted/30"
                >
                  {toolCallsOpen ? (
                    <ChevronDown className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
                  )}
                  <Wrench className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                  <span className="text-foreground font-medium">
                    {message.toolCalls!.length} tool call
                    {message.toolCalls!.length > 1 ? "s" : ""}
                  </span>
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2 space-y-2">
                {message.toolCalls!.map((toolCall) => (
                  <div
                    key={toolCall.id}
                    className="rounded-md border border-border/50 bg-muted/20 p-2.5 text-xs min-w-0"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Badge
                        variant="outline"
                        className="text-xs font-mono bg-background/80 border-border/50"
                      >
                        {toolCall.name}
                      </Badge>
                    </div>
                    {toolCall.arguments && Object.keys(toolCall.arguments).length > 0 && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-muted-foreground hover:text-foreground text-xs font-medium mb-1.5">
                          Arguments
                        </summary>
                        <div className="mt-1.5 rounded-md bg-muted/40 border border-border/30 p-2.5">
                          <pre className="text-xs overflow-x-auto break-words whitespace-pre-wrap font-mono leading-relaxed text-foreground/90">
                            {JSON.stringify(toolCall.arguments, null, 2)}
                          </pre>
                        </div>
                      </details>
                    )}
                  </div>
                ))}
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}

        {isTool && hasContent && (
          <div>
            <Collapsible open={toolResultOpen} onOpenChange={setToolResultOpen}>
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto px-2.5 py-2 text-xs font-medium hover:bg-muted/60 w-full justify-start rounded-md border border-border/50 bg-muted/30"
                >
                  {toolResultOpen ? (
                    <ChevronDown className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
                  )}
                  <Code2 className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                  <span className="text-foreground font-medium">
                    Tool result
                    {isJsonResult ? (
                      <span className="ml-1.5 text-muted-foreground font-normal">(JSON)</span>
                    ) : (
                      ""
                    )}
                  </span>
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2">
                <div className="rounded-md border border-border/50 bg-muted/20 p-3 max-h-96 overflow-auto min-w-0 break-words">
                  {isJsonResult ? (
                    <pre className="text-xs overflow-x-auto break-words whitespace-pre-wrap overflow-wrap-anywhere font-mono leading-relaxed text-foreground/90">
                      {JSON.stringify(toolResultData, null, 2)}
                    </pre>
                  ) : (
                    <div className="text-xs whitespace-pre-wrap break-words overflow-wrap-anywhere leading-relaxed text-foreground/90">
                      {message.content}
                    </div>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}

        {shouldShowContent && (
          <div
            className={cn(
              "bg-primary/50 p-3 rounded-md",
              "prose prose-sm dark:prose-invert max-w-none min-w-0",
              "prose-headings:font-semibold prose-headings:mt-4 prose-headings:mb-2",
              "prose-p:leading-relaxed prose-p:mb-3",
              "prose-ul:my-2 prose-ol:my-2",
              "prose-li:my-1",
              isUser && "prose-invert"
            )}
          >
            {isAssistant ? (
              <div className="markdown-content break-words overflow-wrap-anywhere text-sm leading-relaxed">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    p: ({ children }) => (
                      <p className="mb-2.5 last:mb-0 break-words leading-relaxed text-foreground">
                        {children}
                      </p>
                    ),
                    h1: ({ children }) => (
                      <h1 className="text-lg font-semibold mt-4 mb-2 break-words">{children}</h1>
                    ),
                    h2: ({ children }) => (
                      <h2 className="text-base font-semibold mt-4 mb-2 break-words">{children}</h2>
                    ),
                    h3: ({ children }) => (
                      <h3 className="text-sm font-semibold mt-3 mb-2 break-words">{children}</h3>
                    ),
                    ul: ({ children }) => (
                      <ul className="list-disc list-outside ml-4 mb-2.5 space-y-1 break-words">
                        {children}
                      </ul>
                    ),
                    ol: ({ children }) => (
                      <ol className="list-decimal list-outside ml-4 mb-2.5 space-y-1 break-words">
                        {children}
                      </ol>
                    ),
                    li: ({ children }) => (
                      <li className="break-words leading-relaxed text-foreground">{children}</li>
                    ),
                    code: ({ className, children, ...props }) => {
                      const isInline = !className || !className.includes("language-")
                      return isInline ? (
                        <code
                          className="px-1.5 py-0.5 rounded bg-muted/70 text-xs font-mono break-words border border-border/50"
                          {...props}
                        >
                          {children}
                        </code>
                      ) : (
                        <div className="my-3 rounded-lg border bg-muted/30 overflow-hidden">
                          <code
                            className="block p-3 text-xs font-mono overflow-x-auto break-words whitespace-pre-wrap leading-relaxed"
                            {...props}
                          >
                            {children}
                          </code>
                        </div>
                      )
                    },
                    table: ({ children }) => (
                      <div className="overflow-x-auto my-4 rounded-lg border border-border/50">
                        <table className="min-w-full border-collapse text-sm">{children}</table>
                      </div>
                    ),
                    thead: ({ children }) => (
                      <thead className="bg-muted/50 border-b">{children}</thead>
                    ),
                    tbody: ({ children }) => <tbody>{children}</tbody>,
                    tr: ({ children }) => (
                      <tr className="border-b border-border/30 last:border-0">{children}</tr>
                    ),
                    th: ({ children }) => (
                      <th className="px-3 py-2 text-left font-semibold text-xs uppercase tracking-wider">
                        {children}
                      </th>
                    ),
                    td: ({ children }) => (
                      <td className="px-3 py-2 text-sm break-words">{children}</td>
                    ),
                    blockquote: ({ children }) => (
                      <blockquote className="border-l-4 border-muted-foreground/30 pl-4 my-3 italic text-muted-foreground">
                        {children}
                      </blockquote>
                    ),
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              </div>
            ) : (
              <div className="whitespace-pre-wrap break-words overflow-wrap-anywhere text-sm leading-relaxed">
                {message.content}
              </div>
            )}
          </div>
        )}
        {message.timestamp && (
          <span className="text-[10px] text-muted-foreground/70 mt-1 block">
            {new Date(message.timestamp).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            })}
          </span>
        )}
      </div>
    </div>
  )
}
